/* MapCase.js */

// JSON packets from server have snake_cased fields. Allow destructuring
/* eslint camelcase: [ "error", { properties: "never", allow: ["molecule_name", "gene_name"]}] */

import { ScopedPropertyGroup, fetchProperty, fetchPropertyGroup } from 'BMapsModel';

export class MapCase {
    constructor(obj) {
        /** @type {string} */
        this.molecule_name = obj.molecule_name;
        /** @type {string} */
        this.pdbID = obj.pdbID;
        this.description = obj.description;
        this.fragSpecs = obj.fragSpecs;
        /** @type {string} */
        this.uri = obj.uri; // May include mount index
        this.gene_name = obj.gene_name;
        this.project = obj.project;
        this.case = obj.case;
        /** @type {string} */
        this.projectCase = this.project && this.case ? `${this.project}/${this.case}`: ''; // Like uri, but without mount
        this.group_name = obj.group_name || obj.molecule_name;
        this.organism = obj.organism;
        this.sourceDesc = obj.sourceDesc; // Note about the origin
        this.lastUpdate = obj.lastUpdate || null;
        this.data = obj.data; // pdb data loaded by user
        this.data_format = obj.data_format; // format of user data (pdb or mol2)
        /** @type { import('BMapsModel').CaseDataCollection} */
        this.caseDataCollection = null;
        /** @type {DataSource} */
        this.dataSource = null;
        this.otherProperties = obj.otherProperties;
        this.scopedProperties = new ScopedPropertyGroup('target');
    }

    getDataSource() { return this.dataSource; }
    setDataSource(dataSource) { this.dataSource = dataSource; }
    getCaseDataCollection() { return this.caseDataCollection; }
    setCaseDataCollection(caseDataCollection) { this.caseDataCollection = caseDataCollection; }

    getProperty(scope, prop) { return this.scopedProperties.getPropertyValue(scope, prop); }
    getPropertyInfo(scope, prop) { return this.scopedProperties.getPropertyInfo(scope, prop); }
    setProperty(scope, prop, value) { this.scopedProperties.setPropertyValue(scope, prop, value); }
    getPropertyGroup(scope) { return this.scopedProperties.getScope(scope); }
    listProperties(listPropertiesParams) {
        return this.scopedProperties.listProperties(listPropertiesParams);
    }

    async fetchProperty(scope, prop, fn, trigger) {
        const propRef = this.scopedProperties.ensureProperty(scope, prop);
        return fetchProperty(propRef, fn, trigger);
    }

    async fetchPropertyGroup(scope, fn, trigger) {
        const groupRef = this.scopedProperties.ensureScope(scope);
        return fetchPropertyGroup(groupRef, fn, trigger);
    }

    static externalLinkInfo() { return null; }
    myExternalLinkInfo() {
        if (PdbImportCase.isValidId(this.pdbID)) {
            return PdbImportCase.externalLinkInfo(this.pdbID);
        }
        return MapCase.externalLinkInfo(this.pdbID);
    }

    hasDefaultMoleculeName() { return false; }

    /** @returns {string} */
    get displayName() {
        return this.molecule_name
            || this.pdbID
            || this.projectCase
            || this.gene_name
            || this.description
            || '(unnamed target)';
    }

    get loadingMessage() { return 'Loading protein and fragment data...'; }

    getNodeName() {
        return (
            this.project
                ? this.projectCase
                : (this.pdbID || this.molecule_name || 'Unnamed structure')
        );
    }

    getLongName() {
        const protName = this.molecule_name || this.description || '';
        const pdbID = this.pdbID || '';
        const pdbDisplay = (protName && pdbID) ? ` (${pdbID})` : pdbID;
        return `${protName}${pdbDisplay}`;
    }

    getShortName() {
        return this.pdbID || this.displayName;
    }

    static parseFragSpecs(spec) {
        //--- TBI
        return spec;
    }

    // Convert bfd-server maplist object to local class. Mostly passthrough (except pdbid)
    // Note: uri and lastUpdate are not part of the entries in the map-list.json file:
    // * uri is calculated here using the datasource mountIndex
    // * lastUpdate is added to the entries dynamically by bfd-server
    static fromBfdServer(map, dataSource) {
        const requiredFields = ['molecule_name', 'pdbid', 'description'];
        if (requiredFields.some((field) => map[field] == null)) {
            console.warn(`Ill-formatted map case obj: ${JSON.stringify(map)}`);
            return null;
        }

        let uri = `${map['project']}/${map['case']}`;
        if (dataSource?.mountIndex != null) {
            uri += ` mount${dataSource.mountIndex}`;
        }

        // lastUpdate from bfd server is a unix timestamp (seconds from epoch)
        // Multiply by 1000 since JS Date object expects ms from epoch
        const lastUpdate = map.lastUpdate != null ? new Date(map.lastUpdate * 1000) : null;
        const mapCase = new MapCase({
            molecule_name: map['molecule_name'],
            pdbID: map['pdbid'], // note case difference
            description: map['description'],
            fragSpecs: null, // NYI on server
            uri,
            gene_name: map['gene_name'],
            project: map['project'],
            case: map['case'],
            sourceDesc: map['sourceDesc'] || MapCase.Sources.Public,
            organism: map['organism'],
            group_name: map['group_name'],
            lastUpdate,
            otherProperties: map['otherProperties'],
        });
        mapCase.setDataSource(dataSource);
        return mapCase;
    }

    /**
     * Parse a project case string into project and case components
     * Test in MapCase.test.js
     * @param {string} projectCase
     * @returns {{ project: string?, case: string? }}
     */
    static splitProjectCase(projectCase) {
        const projectCasePattern = /^(?<project>[^\s/]+)\/(?<case>[^\s/]+)$/;
        const match = projectCase.match(projectCasePattern);
        return {
            project: match?.groups?.project || null,
            case: match?.groups?.case || null,
        };
    }

    /**
     * Parse a mapcase uri into projectCase and mountIndex components
     * Test in MapCase.test.js
     * @param {string} uri
     * @returns {{ projectCase: string?, mountIndex: string?, project: string?, case: string? }}
     */
    static splitUri(uri) {
        const ret = {
            projectCase: null,
            mountIndex: null,
            project: null,
            case: null,
        };

        const uriPattern = /^(?<projectCase>[^\s]+)( (?<mountIndex>mount\d+))?$/;
        const match = uri.match(uriPattern);
        if (!match) return ret;

        const { projectCase, mountIndex } = match.groups;
        ret.projectCase = projectCase;
        ret.mountIndex = mountIndex || null;

        const { project, case: caseName } = MapCase.splitProjectCase(projectCase);
        ret.project = project;
        ret.case = caseName;
        return ret;
    }
}

export class PdbImportCase extends MapCase {
    constructor(pdbID) {
        super({
            pdbID,
            group_name: MapCase.GroupNames.PdbImports,
            molecule_name: '',
            description: '',
            sourceDesc: MapCase.Sources.PDB,
            fragSpecs: '',
            uri: `pdb:${pdbID}`,
        });
    }

    get displayName() { return this.pdbID; }
    get loadingMessage() { return `Importing ${this.displayName} from the PDB`; }

    getNodeName() {
        return `PDB Import: ${this.pdbID}`;
    }

    // Number followed by 3 alphanumerics, with at least one letter
    static isValidId(id) {
        const pidFormat = /^[0-9][A-Za-z0-9]{3}$/;
        const alphaChar = /[A-Za-z]/;
        return pidFormat.test(id) && alphaChar.test(id);
    }

    static isUri(uri) {
        const pattern = /^pdb:/;
        return pattern.test(uri);
    }

    static parseUri(uri) {
        return {
            id: uri.substr('pdb:'.length),
        };
    }

    static externalLinkInfo(id) {
        return {
            url: `http://www.rcsb.org/pdb/explore/explore.do?structureId=${id}`,
            label: 'PDB',
            title: `Go to the original ${id} structure at the PDB`,
            faIcon: 'fa-external-link',
        };
    }

    myExternalLinkInfo() {
        return PdbImportCase.externalLinkInfo(this.pdbID);
    }

    static newRecordMsgInfo(id) {
        return {
            description: 'Use original structure from the protein data bank (no simulation data available)',
            itemName: `Import ${id}`,
        };
    }
}

export class AlphaFoldImportCase extends MapCase {
    constructor(uniprotID) {
        super({
            pdbID: uniprotID,
            group_name: MapCase.GroupNames.AlphaFoldImports,
            molecule_name: '',
            description: '',
            sourceDesc: MapCase.Sources.AlphaFold,
            fragSpecs: '',
            uri: `uniprot:${uniprotID}`,
        });
    }

    get displayName() { return this.pdbID; }
    get loadingMessage() { return `Importing ${this.displayName} from AlphaFold`; }

    getNodeName() {
        return `AlphaFold Import: ${this.pdbID}`;
    }

    static isValidId(id) {
        // From: https://www.uniprot.org/help/accession_numbers
        const format = /^[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/;
        return format.test(id);
    }

    static isUri(uri) {
        const pattern = /^uniprot:/;
        return pattern.test(uri);
    }

    static parseUri(uri) {
        return {
            id: uri.substr('uniprot:'.length),
        };
    }

    static externalLinkInfo(id) {
        return {
            url: `https://alphafold.ebi.ac.uk/entry/${id}`,
            label: 'AlphaFold',
            title: `Go to UniProt:${id} structure at AlphaFold`,
            faIcon: 'fa-external-link',
        };
    }

    myExternalLinkInfo() {
        return AlphaFoldImportCase.externalLinkInfo(this.pdbID);
    }

    static newRecordMsgInfo(id) {
        return {
            description: 'Import AI generated structure from AlphaFold',
            itemName: `AlphaFold import UniProt:${id}`,
        };
    }
}

export class UserDataImportCase extends MapCase {
    static get DefaultRootMolName() { return 'Imported structure'; }

    constructor({
        name, pdbid, data, format, loadOptions,
    }) {
        super({
            group_name: MapCase.GroupNames.UserUploads,
            molecule_name: name || UserDataImportCase.newDefaultMolName(),
            pdbID: pdbid || '',
            description: '',
            sourceDesc: MapCase.Sources.ImportScreen,
            fragSpecs: '',
            uri: UserDataImportCase.newUri(),
            data,
            data_format: format,
        });
        this.loadOptions = loadOptions;
    }

    get loadingMessage() { return 'Importing protein data...'; }

    getNodeName() {
        return `User upload: ${this.pdbID || this.molecule_name}`;
    }

    getShortName() {
        let name = this.molecule_name;
        if (this.hasDefaultMoleculeName()) {
            const idNum = LocalIdManager.ExtractIdNum(this.uri, 'userdata');
            name = `Protein\u00A0${idNum}`; // \u00A0 = nonbreaking space
        }
        return this.pdbID || name;
    }

    hasDefaultMoleculeName() {
        return this.molecule_name === UserDataImportCase.DefaultRootMolName;
    }

    // stuff for user data /
    static newUri() {
        return LocalIdManager.GetNewUri('userdata');
    }

    static isUri(uri) {
        return LocalIdManager.IsUriFor(uri, 'userdata');
    }

    static newDefaultMolName() {
        return UserDataImportCase.DefaultRootMolName;
    }
}

export class FoldingDataImportCase extends MapCase {
    static get DefaultRootMolName() { return 'Folded structure'; }
    constructor({
        name, data, format,
        foldingData, loadOptions,
        resultProperties,
    }) {
        const uri = FoldingDataImportCase.newUri();
        const idNum = LocalIdManager.ExtractIdNum(uri, 'folding');
        super({
            group_name: MapCase.GroupNames.UserFolded,
            molecule_name: name || FoldingDataImportCase.newDefaultMolName(idNum),
            pdbID: '',
            description: `Folded with ${foldingData.foldingProgramId}`,
            fragSpecs: '',
            sourceDesc: `Folded in BMaps with ${foldingData.foldingProgramId}`,
            uri,
            data,
            data_format: format,
        });
        this.foldingData = foldingData;
        this.loadOptions = loadOptions;
        this.resultProperties = resultProperties;
    }

    get loadingMessage() { return 'Loading results of protein folding...'; }
    get localIdNum() { return LocalIdManager.ExtractIdNum(this.uri, 'folding'); }

    getShortName() {
        return `Folded ${this.localIdNum}`;
    }

    hasDefaultMoleculeName() {
        const pattern = new RegExp(`${FoldingDataImportCase} \\d+`);
        return pattern.test(this.molecule_name);
    }

    static newUri() {
        return LocalIdManager.GetNewUri('folding');
    }

    static isUri(uri) {
        return LocalIdManager.IsUriFor(uri, 'folding');
    }

    static newDefaultMolName(idNum) {
        const baseName = FoldingDataImportCase.DefaultRootMolName;
        return `${baseName} ${idNum}`;
    }

    myExternalLinkInfo() {
        const program = this.foldingData.foldingProgramId;
        return {
            label: program,
            title: `Download ${program} folding results`,
            faIcon: 'fa-download',
            download: {
                data: this.data,
                filename: `BMaps_${program}_results.${this.data_format}`,
            },
        };
    }
}

MapCase.GroupNames = {
    PdbImports: 'PDB Imports',
    UserUploads: 'User Uploads',
    AlphaFoldImports: 'AlphaFold Imports',
    UserFolded: 'Folded by User',
};
MapCase.Sources = {
    Public: 'bmaps_public', // Located in public bmaps repo
    Private: 'bmaps_private', // Located in a private, managed repo
    PDB: 'Imported from protein data bank', // Pulled from PDB
    ImportScreen: 'Import screen', // Added with Import tool
    Fragserv: 'fragserv', // Created with fragment service, and loaded from user repo
    AlphaFold: 'Imported from AlphaFold', // Pulled from AlphaFold
    KinasePrep: 'kinase-prep', // Kinase selectivity library (server-side, may have fragment data)
};

class LocalIdManager {
    static GetNewId(key) {
        if (!LocalIdManager.counters) LocalIdManager.counters = {};
        if (!LocalIdManager.counters[key]) LocalIdManager.counters[key] = 1;
        return LocalIdManager.counters[key]++;
    }

    static GetNewUri(key) {
        const id = LocalIdManager.GetNewId(key);
        return `local:${key}${id}`;
    }

    static IsUriFor(uri, key) {
        const pattern = new RegExp(`^local:${key}(?<number>\\d+)$`);
        return pattern.test(uri);
    }

    static ExtractIdNum(uri, key) {
        const pattern = new RegExp(`^local:${key}(?<number>\\d+)$`);
        const match = uri.match(pattern)?.groups.number;
        return match;
    }
}
